import { ChatMessage } from '@aimi/models';
import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  ChatConversationAddMessage,
  ChatConversationCreateMessage,
  ChatConversationGet,
  ChatConversationReset,
  ChatConversationUpdateMessage,
} from './chat-conversation.action';
import { LoadingEnd, LoadingStart } from '../../../core/loading.state';
import { ChatService } from '../chat.service';
import { finalize, switchMap, tap } from 'rxjs';
import { append, patch, updateItem } from '@ngxs/store/operators';
import {
  SocketIoJoinChat,
  SocketIoLeaveChat,
} from '../../socket-io/socket-io.action';

interface ChatConversationStateModel {
  messages: ChatMessage[];
  chatId: string | undefined;
}

@State<ChatConversationStateModel>({
  name: 'ChatConversationState',
  defaults: {
    messages: [],
    chatId: undefined,
  },
})
@Injectable()
export class ChatConversationState {
  #chatService = inject(ChatService);

  @Selector() static chatId(state: ChatConversationStateModel) {
    return state.chatId;
  }

  @Selector() static chatMessages(state: ChatConversationStateModel) {
    return state.messages;
  }

  @Action(ChatConversationGet) getChatConversations(
    ctx: StateContext<ChatConversationStateModel>,
    action: ChatConversationGet,
  ) {
    ctx.dispatch(new LoadingStart(this.constructor.name));
    ctx.patchState({
      chatId: action.chatId,
    });
    return this.#chatService.getChatMessages(action).pipe(
      tap((chatMessages) => {
        ctx.patchState({
          messages: chatMessages,
        });
      }),
      switchMap(() => {
        return ctx.dispatch(new SocketIoJoinChat(action.chatId));
      }),
      finalize(() => ctx.dispatch(new LoadingEnd(this.constructor.name))),
    );
  }

  @Action(ChatConversationCreateMessage) createChatMessage(
    ctx: StateContext<ChatConversationStateModel>,
    action: ChatConversationCreateMessage,
  ) {
    ctx.dispatch(new LoadingStart(this.constructor.name));
    return this.#chatService
      .createChatMessage(action.chatId, {
        message: action.message,
      })
      .pipe(
        tap((chatMessage) => {
          ctx.setState(
            patch({
              messages: append([chatMessage]),
            }),
          );
        }),
        finalize(() => ctx.dispatch(new LoadingEnd(this.constructor.name))),
      );
  }

  @Action(ChatConversationReset) reset(
    ctx: StateContext<ChatConversationStateModel>,
  ) {
    const currentState = ctx.getState();

    ctx.setState({
      chatId: undefined,
      messages: [],
    });
    if (currentState.chatId) {
      ctx.dispatch(new SocketIoLeaveChat(currentState.chatId));
    }
  }

  @Action(ChatConversationAddMessage)
  addMessage(
    ctx: StateContext<ChatConversationStateModel>,
    action: ChatConversationAddMessage,
  ) {
    const { chatId } = ctx.getState();
    if (chatId !== action.chatMessage.chatId) {
      return;
    }
    ctx.setState(
      patch({
        messages: append([action.chatMessage]),
      }),
    );
  }

  @Action(ChatConversationUpdateMessage)
  updateMessage(
    ctx: StateContext<ChatConversationStateModel>,
    action: ChatConversationUpdateMessage,
  ) {
    const { chatId } = ctx.getState();
    if (chatId !== action.chatMessage.chatId) {
      return;
    }

    ctx.setState(
      patch({
        messages: updateItem<ChatMessage>(
          (cm) => cm.id === action.chatMessage.id,
          patch({ message: action.chatMessage.message }),
        ),
      }),
    );
  }
}
